import { Mic, Square } from 'lucide-react'
import { useEffect, useRef, type RefObject } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { toastUndo } from '@/lib/toastUndo'
import type { InputMethod } from './types'
import { useSpeechDictation } from './useSpeechDictation'

interface Props {
  value: string
  onChange: (value: string) => void
  /** Reports how the text came about: spoken, typed or both (telemetry, no content). */
  onInputMethod?: ((method: InputMethod) => void) | undefined
  inputMethod?: InputMethod | null
  lang?: string
  disabled?: boolean
  /** Tells the page when the microphone starts and stops listening. */
  onListeningChange?: ((listening: boolean) => void) | undefined
  /** Filled with a function that stops the recording (the page's "Verwerken" stops first). */
  stopRef?: RefObject<(() => void) | null> | undefined
}

const formatTime = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

/**
 * The memo field with the record button. Read-only while listening; final sentences are
 * appended, the interim text stays grey below. "Wissen" can be undone from the toast.
 */
export function DictationField({
  value,
  onChange,
  onInputMethod,
  inputMethod = null,
  lang,
  disabled = false,
  onListeningChange,
  stopRef,
}: Props) {
  const valueRef = useRef(value)
  useEffect(() => {
    valueRef.current = value
  })
  const spokeRef = useRef(inputMethod === 'spraak' || inputMethod === 'gemengd')
  const typedRef = useRef(inputMethod === 'toetsenbord' || inputMethod === 'gemengd')
  const reportedRef = useRef<InputMethod | null>(inputMethod)
  const fieldRef = useRef<HTMLTextAreaElement>(null)

  const report = () => {
    const method: InputMethod = spokeRef.current
      ? typedRef.current
        ? 'gemengd'
        : 'spraak'
      : 'toetsenbord'
    if (method !== reportedRef.current) {
      reportedRef.current = method
      onInputMethod?.(method)
    }
  }

  const dictation = useSpeechDictation({
    ...(lang ? { lang } : {}),
    onFinal: (text) => {
      const current = valueRef.current
      const next = current && !/\s$/.test(current) ? `${current} ${text}` : `${current}${text}`
      valueRef.current = next
      spokeRef.current = true
      onChange(next)
      report()
    },
  })
  const listening = dictation.state === 'luistert'
  const keyboardMode = dictation.mode === 'toetsenbord'

  const { stop } = dictation
  useEffect(() => {
    onListeningChange?.(listening)
  }, [listening, onListeningChange])
  useEffect(() => {
    if (!stopRef) return
    stopRef.current = stop
    return () => {
      stopRef.current = null
    }
  }, [stop, stopRef])

  useEffect(() => {
    if (keyboardMode) fieldRef.current?.focus()
  }, [keyboardMode])

  const clear = () => {
    const previous = value
    onChange('')
    toastUndo('Tekst gewist.', () => onChange(previous))
  }

  return (
    <div className="space-y-3">
      {keyboardMode ? (
        <p className="rounded-lg border border-border bg-muted p-3 text-sm" role="note">
          {dictation.message ? `${dictation.message} ` : ''}
          Tik op de microfoon van je toetsenbord om te dicteren, of typ je verslag.
        </p>
      ) : (
        <div className="flex flex-col items-center gap-2">
          {listening ? (
            <>
              <p aria-live="polite" className="text-base">
                ● Luistert… {formatTime(dictation.elapsedSeconds)}
              </p>
              <Button variant="outline" size="lg" onClick={dictation.stop}>
                <Square aria-hidden className="size-4" /> Stop
              </Button>
            </>
          ) : (
            <>
              <Button
                aria-label={dictation.canResume ? 'Verder inspreken' : 'Tik om in te spreken'}
                className="size-18 max-md:size-18"
                disabled={disabled}
                onClick={dictation.start}
              >
                <Mic aria-hidden className="size-8" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {dictation.canResume ? 'Verder inspreken' : 'Tik om in te spreken'}
              </span>
              {dictation.message && (
                <p role="status" className="text-center text-sm text-muted-foreground">
                  {dictation.message}
                </p>
              )}
            </>
          )}
        </div>
      )}
      <Textarea
        ref={fieldRef}
        aria-label="Tekst van je verslag"
        placeholder="Of typ hier…"
        rows={8}
        value={value}
        readOnly={listening}
        disabled={disabled}
        onChange={(event) => {
          typedRef.current = true
          onChange(event.target.value)
          report()
        }}
      />
      <div aria-live="polite" className="min-h-5 text-sm text-muted-foreground">
        {dictation.interim}
      </div>
      <Button variant="ghost" size="sm" disabled={listening || disabled || value === ''} onClick={clear}>
        Wissen
      </Button>
    </div>
  )
}
